import { useState } from "react";
import { ApiError } from "../lib/api";
import { formatDockerSize, parseDockerSize, totalDockerSizes } from "../lib/dockerSize";
import { formatDateTime, formatRelative } from "../lib/format";
import { useRemoveVolumes, useServerVolumes } from "../lib/queries";
import type { OrphanVolume, PendingVolumeRemoval, VolumeRemovalResult } from "../lib/types";
import { Modal } from "./Modal";
import { OtpCells } from "./OtpCells";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  LoadingBlock,
  Mono,
  Table,
  Tag,
  Td,
  Th,
  TypedPhrase,
  cx,
} from "./ui";

/**
 * Data volumes that deleted apps and databases left on a server.
 *
 * Dokploy's delete removes the service and leaves its volumes on the disk, so
 * servers filled with the data of databases customers deleted long ago. New
 * deletes queue their volumes for the sweep; this card is for what predates
 * that, and for watching the queue. Which volumes are orphans is the API's
 * call, made against every live service on the engine — the card lists what
 * it is given and filters nothing.
 *
 * Unlike "Remove unused volumes" in the cleanup card, nothing here can take a
 * stopped database: a volume any service owns is never listed, and the API
 * checks each name again before it removes it.
 */

/** The API takes at most this many names in one request. */
const REMOVE_LIMIT = 200;

const KIND_LABEL: Record<PendingVolumeRemoval["workloadKind"], string> = {
  app: "app",
  database: "database",
  orphan: "orphan",
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

const CHECKBOX = "h-4 w-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20";

export function OrphanedVolumesCard({
  serverId,
  serverName,
  canWrite,
}: {
  serverId: string;
  serverName: string;
  canWrite: boolean;
}) {
  const volumes = useServerVolumes(serverId);
  const remove = useRemoveVolumes(serverId);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // What the dialog is removing, fixed when it opens: the list refetches the
  // moment the request answers, and the report must still name what went.
  const [removing, setRemoving] = useState<OrphanVolume[] | null>(null);
  const [results, setResults] = useState<VolumeRemovalResult[] | null>(null);

  const data = volumes.data;
  const orphans = data?.available ? data.orphans : [];
  // By name against the latest scan, so a volume that has since left the list
  // drops out of the selection rather than being sent.
  const chosen = orphans.filter((orphan) => selected.has(orphan.name));
  const all = orphans.length > 0 && chosen.length === orphans.length;

  function toggle(name: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  return (
    <Card>
      <CardHeader
        title="Orphaned volumes"
        description="Data volumes deleted apps and databases left on this server, read live."
        action={
          <Button
            variant="secondary"
            size="sm"
            loading={volumes.isFetching}
            onClick={() => void volumes.refetch()}
          >
            Scan again
          </Button>
        }
      />

      {volumes.error && (
        <div className="px-5 pt-4">
          <Alert>{volumes.error.message}</Alert>
        </div>
      )}

      {volumes.isPending ? (
        <LoadingBlock label="Scanning the server…" />
      ) : !data ? null : !data.available ? (
        <div className="p-5">
          <Alert tone="info">{data.reason}</Alert>
        </div>
      ) : orphans.length === 0 ? (
        <p className="px-5 py-4 text-[13px] text-fg-muted">
          Nothing orphaned: every Maxlayer data volume here belongs to a live service. Scanned{" "}
          {formatRelative(data.scannedAt)}.
        </p>
      ) : (
        <>
          {/*
            The action sits in the card rather than in the pinned selection
            bar: that bar is for a selection spanning cards, and its spacer
            would open a gap in the middle of this tab.
          */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-line px-5 py-4">
            <OrphanSummary orphans={orphans} scannedAt={data.scannedAt} />
            {canWrite && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:ml-auto">
                {chosen.length > 0 && (
                  <span className="text-xs text-fg-subtle">
                    {chosen.length > REMOVE_LIMIT
                      ? `Up to ${REMOVE_LIMIT} at a time.`
                      : `${chosen.length} selected · ${sizeOf(chosen)}`}
                  </span>
                )}
                <Button
                  variant="danger"
                  size="sm"
                  icon="trash"
                  disabled={chosen.length === 0 || chosen.length > REMOVE_LIMIT}
                  onClick={() => setRemoving(chosen)}
                >
                  {chosen.length > 0 ? `Remove ${chosen.length}…` : "Remove…"}
                </Button>
              </div>
            )}
          </div>

          <Table
            head={
              <tr>
                {canWrite && (
                  <Th className="w-8">
                    <input
                      type="checkbox"
                      aria-label="Select every orphaned volume"
                      checked={all}
                      onChange={() => setSelected(all ? new Set() : new Set(orphans.map((o) => o.name)))}
                      className={CHECKBOX}
                    />
                  </Th>
                )}
                <Th>Volume</Th>
                <Th numeric priority="sm">
                  Size
                </Th>
              </tr>
            }
          >
            {orphans.map((orphan) => (
              <tr key={orphan.name} className="hover:bg-raised">
                {canWrite && (
                  <Td>
                    <input
                      type="checkbox"
                      aria-label={`Select ${orphan.name}`}
                      checked={selected.has(orphan.name)}
                      onChange={() => toggle(orphan.name)}
                      className={CHECKBOX}
                    />
                  </Td>
                )}
                <Td>
                  <Mono className="text-fg">{orphan.name}</Mono>
                  {/* The size rides here below `sm`, where its own column is
                      dropped: on a phone it is the figure somebody came for. */}
                  <p className={cx("mt-0.5 text-xs text-fg-subtle", !orphan.formerName && "sm:hidden")}>
                    {orphan.formerName && (
                      <>
                        was <span className="font-mono">{orphan.formerName}</span>
                      </>
                    )}
                    <span className="sm:hidden">
                      {orphan.formerName && " · "}
                      <SizeText size={orphan.size} />
                    </span>
                  </p>
                </Td>
                <Td numeric priority="sm">
                  <SizeText size={orphan.size} />
                </Td>
              </tr>
            ))}
          </Table>

          {!canWrite && (
            <p className="border-t border-line px-5 py-3 text-xs text-fg-subtle">
              Removing them needs superadmin access.
            </p>
          )}
        </>
      )}

      {data && <PendingRemovals pending={data.pending} />}

      <RemoveVolumesModal
        open={removing !== null}
        serverName={serverName}
        volumes={removing ?? []}
        pending={remove.isPending}
        results={results}
        run={async (otp) => {
          const names = (removing ?? []).map((volume) => volume.name);
          const response = await remove.mutateAsync({ names, otp });
          setResults(response.results);
        }}
        onClose={() => setRemoving(null)}
        onDone={() => {
          setRemoving(null);
          setResults(null);
          setSelected(new Set());
        }}
      />
    </Card>
  );
}

/**
 * The figure first, because "how much would this give back" is the question.
 * Summed only from what Docker measured: one it did not makes the total a
 * floor, and it says so rather than estimating the rest.
 */
function OrphanSummary({ orphans, scannedAt }: { orphans: OrphanVolume[]; scannedAt: string }) {
  const total = totalDockerSizes(orphans.map((orphan) => orphan.size));
  const unmeasured = orphans.filter((orphan) => parseDockerSize(orphan.size) === null).length;
  const count = total ? `in ${plural(orphans.length, "volume")}` : plural(orphans.length, "volume");

  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      {total && (
        <p className="text-2xl font-light tabular-nums tracking-tight text-fg">
          {total.partial && <span className="mr-1.5 text-sm text-fg-subtle">at least</span>}
          {formatDockerSize(total.bytes)}
        </p>
      )}
      <p className="text-xs text-fg-subtle">
        {count}
        {unmeasured > 0 && (total ? `, ${unmeasured} not measured` : ", none measured")} · scanned{" "}
        {formatRelative(scannedAt)}
      </p>
    </div>
  );
}

/** Docker's own string, as written; a dash where it measured nothing. */
function SizeText({ size }: { size: string | null }) {
  if (size === null) {
    return (
      <span className="text-fg-subtle" title="Docker did not measure this volume">
        —
      </span>
    );
  }
  return <span className="tabular-nums">{size}</span>;
}

function sizeOf(volumes: OrphanVolume[]): string {
  const total = totalDockerSizes(volumes.map((volume) => volume.size));
  if (!total) return "size not measured";
  return `${total.partial ? "at least " : ""}${formatDockerSize(total.bytes)}`;
}

/**
 * The queue is the platform's own rows rather than the engine's, so it is
 * listed even when the server could not be scanned. Each row is a removal the
 * sweep will try again; the error is what Docker said the last time.
 */
function PendingRemovals({ pending }: { pending: PendingVolumeRemoval[] }) {
  return (
    <div className="space-y-3 border-t border-line px-5 py-4">
      <div>
        <p className="text-sm font-medium text-fg">
          Queued for removal{pending.length > 0 && ` · ${pending.length}`}
        </p>
        <p className="mt-0.5 text-xs leading-5 text-fg-subtle">
          {pending.length === 0
            ? "Nothing waiting."
            : "Retried every five minutes. In use for a few minutes after a delete is normal: the container is still stopping."}
        </p>
      </div>

      {pending.length > 0 && (
        <ul className="divide-y divide-line rounded-md border border-line">
          {pending.map((removal) => (
            <li key={removal.id} className="space-y-1 px-3 py-2.5">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <Mono className="text-sm text-fg">{removal.volumeName}</Mono>
                <Tag>{KIND_LABEL[removal.workloadKind]}</Tag>
                {removal.workloadName && (
                  <span className="text-xs text-fg-muted">{removal.workloadName}</span>
                )}
              </div>
              <p className="text-xs text-fg-subtle">
                <span title={formatDateTime(removal.requestedAt)}>
                  requested {formatRelative(removal.requestedAt)}
                </span>{" "}
                · {plural(removal.attempts, "attempt")}
                {removal.lastAttemptAt && (
                  <span title={formatDateTime(removal.lastAttemptAt)}>
                    , last {formatRelative(removal.lastAttemptAt)}
                  </span>
                )}
              </p>
              {removal.lastError && <p className="text-xs break-words text-warn">{removal.lastError}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ the dialog */

const OUTCOME: Record<VolumeRemovalResult["outcome"], { label: string; tone: string; note: string }> = {
  removed: { label: "Removed", tone: "text-mint", note: "Deleted now." },
  pending: {
    label: "Queued",
    tone: "text-warn",
    note: "Refused for now. The sweep tries again every five minutes.",
  },
  skipped: { label: "Skipped", tone: "text-fg-muted", note: "No longer an orphan. Nothing was done." },
  missing: { label: "Already gone", tone: "text-fg-muted", note: "Docker had no volume by this name." },
};

/**
 * The confirmation, then what each name came to.
 *
 * Two things typed, as on every other delete of customer data here: the
 * server's name, because the console is often open on several, and the
 * operator's authenticator code, which the API checks on this request. It
 * answers 200 whatever happened to each name, so the dialog stays open on the
 * result and goes through it name by name — a shorter list after a spinner
 * would not say which were queued and which were skipped.
 */
export function RemoveVolumesModal({
  open,
  serverName,
  volumes,
  pending,
  results,
  run,
  onClose,
  onDone,
}: {
  open: boolean;
  serverName: string;
  volumes: OrphanVolume[];
  pending: boolean;
  /** Filled once the request answers; the dialog then reports. */
  results: VolumeRemovalResult[] | null;
  run: (otp: string) => Promise<unknown>;
  onClose: () => void;
  /** Closing after the report, which is when the selection is cleared. */
  onDone: () => void;
}) {
  const [typed, setTyped] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState<string | null>(null);
  const ready = volumes.length > 0 && typed.trim() === serverName && otp.length === 6;

  function close() {
    if (pending) return;
    setTyped("");
    setOtp("");
    setError(null);
    if (results) onDone();
    else onClose();
  }

  async function submit() {
    setError(null);
    try {
      await run(otp);
    } catch (cause) {
      // A refused code is cleared: the next one is a different code.
      if (cause instanceof ApiError && cause.code === "totp_required") setOtp("");
      setError(cause instanceof ApiError ? cause.message : "The request could not be sent.");
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={`Remove ${plural(volumes.length, "volume")}`}
      description={`From ${serverName}.`}
      dismissable={!pending}
    >
      {results ? (
        <RemovalReport volumes={volumes} results={results} onDone={close} />
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (ready) void submit();
          }}
          className="space-y-5"
        >
          {error && <Alert>{error}</Alert>}

          <ol className="max-h-48 divide-y divide-line overflow-y-auto rounded-md border border-line">
            {volumes.map((volume) => (
              <li key={volume.name} className="flex items-baseline justify-between gap-3 px-3 py-2">
                <Mono className="min-w-0 text-fg">{volume.name}</Mono>
                <span className="shrink-0 text-xs">
                  <SizeText size={volume.size} />
                </span>
              </li>
            ))}
          </ol>

          <p className="text-sm leading-6 text-fg-muted">
            <strong className="font-medium text-danger">
              The data in these volumes is deleted for good and cannot be recovered.
            </strong>{" "}
            Each is checked again first: one a service owns is skipped, and one still in use is
            queued and retried.
          </p>

          <Field
            label={
              <>
                Type <TypedPhrase>{serverName}</TypedPhrase> to confirm
              </>
            }
            htmlFor="remove-volumes-confirm"
            hint="Exactly as shown, including capitals."
          >
            <Input
              id="remove-volumes-confirm"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              autoFocus
            />
          </Field>

          <Field
            label="Authenticator code"
            htmlFor="remove-volumes-otp"
            hint="The six digits from your authenticator app, right now. Six wrong codes in fifteen minutes lock this for a while."
          >
            <OtpCells id="remove-volumes-otp" value={otp} onChange={setOtp} disabled={pending} />
          </Field>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="secondary" onClick={close} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" variant="danger" icon="trash" disabled={!ready} loading={pending}>
              Remove {plural(volumes.length, "volume")}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function RemovalReport({
  volumes,
  results,
  onDone,
}: {
  volumes: OrphanVolume[];
  results: VolumeRemovalResult[];
  onDone: () => void;
}) {
  const sizes = new Map(volumes.map((volume) => [volume.name, volume.size]));
  const count = (outcome: VolumeRemovalResult["outcome"]) =>
    results.filter((result) => result.outcome === outcome).length;

  return (
    <div className="space-y-5">
      <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-line bg-line sm:grid-cols-4">
        {(["removed", "pending", "skipped", "missing"] as const).map((outcome) => (
          <div key={outcome} className="bg-surface px-3.5 py-3">
            <dt className="micro">{OUTCOME[outcome].label}</dt>
            <dd
              className={cx(
                "mt-1 font-mono text-2xl tabular-nums",
                count(outcome) > 0 ? OUTCOME[outcome].tone : "text-fg-subtle",
              )}
            >
              {count(outcome)}
            </dd>
          </div>
        ))}
      </dl>

      <ol className="max-h-64 divide-y divide-line overflow-y-auto rounded-md border border-line">
        {results.map((result) => {
          const size = sizes.get(result.name);
          return (
            <li key={result.name} className="space-y-0.5 px-3 py-2.5">
              <div className="flex items-baseline justify-between gap-3">
                <Mono className="min-w-0 text-fg">{result.name}</Mono>
                <span className={cx("shrink-0 text-xs font-medium", OUTCOME[result.outcome].tone)}>
                  {OUTCOME[result.outcome].label}
                </span>
              </div>
              <p className="text-xs text-fg-subtle">
                {OUTCOME[result.outcome].note}
                {result.outcome === "removed" && size ? ` ${size} freed.` : ""}
              </p>
              {result.error && <p className="text-xs break-words text-warn">{result.error}</p>}
            </li>
          );
        })}
      </ol>

      <div className="flex justify-end">
        <Button type="button" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}
